<template>
  <section class="part-drawer" :class="{ 'is-docked': docked, 'is-dragging': dragH != null }" :style="dragStyle" :role="docked ? 'region' : 'dialog'" :aria-label="title">
    <header v-if="!docked" class="part-drawer--top" @touchstart.passive="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd" @touchcancel="onTouchEnd">
      <button type="button" class="part-drawer--grip" :aria-label="full ? 'Shrink the drawer' : 'Expand the drawer to full screen'" @click="snapTo( full ? 'half' : 'full' )"></button>
      <h2>{{ title }}</h2>
      <span v-if="changing" class="part-drawer--changing">changing <b>{{ changing }}</b></span>
      <button type="button" class="part-drawer--close" aria-label="Close" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </button>
    </header>
    <!-- Docked in the desktop column: compare controls instead of a close button. -->
    <div v-if="docked && $slots.default" class="part-drawer--actions"><slot /></div>

    <div ref="scroll" class="part-drawer--scroll">
      <PartCard
        v-if="card"
        :specs="card"
        :image="cardImage"
        :eyebrow="step.title"
        :describable="!!target && !target.noCages && !target.noPedals"
        :expandable="expandable && !changing"
        @description="$emit('description', target)"
        @expand="$emit( 'more-info' )"
      />

      <!-- Desktop shows the finishes on the preview, under the view buttons. -->
      <FinishChips v-if="colors.length > 1 && !docked" :label="colourLabel" :colors="colors" :value="colorIndex" @input="$emit( 'color', $event )" />

      <div v-if="widthOptions.length" class="part-drawer--option">
        <span class="part-drawer--label">Width</span>
        <div class="part-drawer--chips" role="radiogroup" aria-label="Tyre width">
          <button
            v-for="option in widthOptions"
            :key="option.value"
            type="button"
            role="radio"
            :aria-checked="String( option.value === width )"
            :title="option.text"
            class="part-chip"
            :class="{ 'is-active': option.value === width, 'is-warn': option.text !== `${ option.value } mm` }"
            @click="$emit( 'width', option.value )"
          >{{ option.value }} mm</button>
        </div>
      </div>

      <!-- Desktop: the card above stays put and only this list scrolls. -->
      <div ref="list" class="part-drawer--list-area">
      <div class="part-drawer--list-head">
        <div v-if="categories.length" class="part-drawer--tabs" role="tablist">
          <button
            v-for="option in categories"
            :key="option.value"
            type="button"
            role="tab"
            :aria-selected="String( option.value === category )"
            :class="{ 'is-active': option.value === category }"
            @click="$emit( 'update:category', option.value )"
          >{{ option.text }}</button>
        </div>
        <input
          v-if="items.length > 8"
          v-model="search"
          type="search"
          class="part-drawer--search"
          placeholder="Search models…"
          aria-label="Search component models"
          autocomplete="off"
        >
      </div>

      <!-- Parts with a side-on photo (frames, bars, groupsets…) get picture cards;
           round parts (wheels, tyres) read better as a list. -->
      <ul v-if="tiles" class="part-drawer--list is-grid">
        <li
          v-for="row in rows"
          :key="row.item.id"
          class="part-tile"
          :class="{ 'is-selected': row.index === value, 'is-reference': row.index === reference }"
        >
          <button type="button" class="part-tile--pick" :aria-pressed="String( row.index === value )" @click="$emit( 'input', row.index )">
            <span class="part-tile--image">
              <img v-if="row.src && !unavailable[ row.src ]" :src="row.src" alt="" loading="lazy" decoding="async" @error="$set( unavailable, row.src, true )">
            </span>
            <strong>{{ row.item.name }}</strong>
            <small v-if="row.index === value && row.colourName" class="part-tile--colour">{{ row.colourName }}</small>
            <small v-else-if="row.finishes > 1">{{ row.finishes }} finishes</small>
            <small v-if="row.line">{{ row.line }}</small>
            <small v-if="row.delta" class="part-row--delta" :class="{ win: row.delta.win }">{{ row.delta.label }}</small>
          </button>
          <div v-if="row.swatches.length > 1" class="part-tile--swatches" role="radiogroup" :aria-label="`${ row.item.name } finish`">
            <button
              v-for="swatch in row.swatches"
              :key="swatch.index"
              type="button"
              role="radio"
              :aria-checked="String( row.index === value && swatch.index === colorIndex )"
              :aria-label="swatch.color.colorName || `Colour ${ swatch.index + 1 }`"
              :title="swatch.color.colorName"
              :class="{ 'is-active': row.index === value && swatch.index === colorIndex }"
              @click="$emit( 'pick', { index: row.index, color: swatch.index })"
            >
              <Color :value="[ swatch.color.color, swatch.color.color2 ]" small />
            </button>
          </div>
          <span v-if="changing && row.index === reference" class="part-card--letter part-tile--badge">A</span>
          <span v-else-if="row.index === value" class="part-row--check part-tile--badge" :class="{ 'part-card--letter is-b': changing }">
            <template v-if="changing">B</template>
            <v-icon v-else small dark>mdi-check-bold</v-icon>
          </span>
        </li>
        <li v-if="!rows.length" class="part-drawer--empty">No models match.</li>
      </ul>

      <ul v-else class="part-drawer--list">
        <li v-for="row in rows" :key="row.item.id">
          <button
            type="button"
            class="part-row"
            :aria-pressed="String( row.index === value )"
            :class="{ 'is-selected': row.index === value, 'is-reference': row.index === reference }"
            @click="$emit( 'input', row.index )"
          >
            <span class="part-row--image">
              <img v-if="row.src && !unavailable[ row.src ]" :src="row.src" alt="" loading="lazy" decoding="async" @error="$set( unavailable, row.src, true )">
            </span>
            <span class="part-row--text">
              <strong>{{ row.item.name }}</strong>
              <small v-if="row.line">{{ row.line }}</small>
              <small v-if="row.delta" class="part-row--delta" :class="{ win: row.delta.win }">{{ row.delta.label }}</small>
            </span>
            <span v-if="changing && row.index === reference" class="part-card--letter">A</span>
            <span v-else-if="row.index === value" class="part-row--check" :class="{ 'part-card--letter is-b': changing }">
              <template v-if="changing">B</template>
              <v-icon v-else small dark>mdi-check-bold</v-icon>
            </span>
          </button>
        </li>
        <li v-if="!rows.length" class="part-drawer--empty">No models match.</li>
      </ul>
      </div>
    </div>
  </section>
</template>

<script>
  import { Color } from '@/components';
  import { normalize } from '@/utils';
  import { partWeight } from '@/utils/weight';
  import { weightDelta } from '@/utils/weightDelta';
  import { rowLine, rowImage, finishLabel } from '@/utils/partSpecs';
  import { entryPrice } from '@/utils/composition';
  import PartCard from './PartCard';
  import FinishChips from './FinishChips';

  export default {
    components: { Color, PartCard, FinishChips },
    props: {
      title: String,
      // Desktop: always open in the side column, no sheet header.
      docked: Boolean,
      // Phone sheet dragged up to full screen.
      full: Boolean,
      // The card offers More info (wheel / tyre spotlight).
      expandable: Boolean,
      step: { type: Object, required: true },
      items: { type: Array, default: () => [] },
      // Index (in items) of the part being chosen: the build's part, or B when comparing.
      value: { type: Number, default: null },
      colorIndex: { type: Number, default: 0 },
      // The build's part for this step: deltas are measured against it.
      selection: { type: Object, default: null },
      // Compare mode: index of A and the letter being changed ('B').
      reference: { type: Number, default: null },
      changing: String,
      card: Object,
      categories: { type: Array, default: () => [] },
      category: { type: String, default: 'All' },
      widthOptions: { type: Array, default: () => [] },
      width: Number
    },
    data: () => ({ search: '', unavailable: {}, dragH: null, snapping: false }),
    computed: {
      // While dragged (and while gliding to a stop) the sheet floats over the
      // page at the finger's height; at rest the builder's CSS places it.
      dragStyle() {
        if ( this.dragH == null ) return null;
        return {
          position: 'fixed', left: 0, right: 0, top: 'auto', bottom: 0, zIndex: 20, animation: 'none',
          height: `${ this.dragH }px`,
          borderRadius: this.dragH >= window.innerHeight - 1 ? '0' : null,
          transition: this.snapping ? 'height .28s cubic-bezier(.2, .8, .2, 1), border-radius .28s' : 'none'
        };
      },
      target() {
        return this.value == null ? null : this.items[ this.value ];
      },
      cardImage() {
        return this.target ? rowImage( this.step.title, this.target, this.colorIndex, true ) : '';
      },
      // Same order as the preview swatches (selectedColor indexes this list).
      colors() {
        const item = this.target;
        if ( !item || item.noCages || item.noPedals ) return [];
        return ( item.colors || [] ).map( a => a.color ).sort(( a, b ) => a.priority - b.priority );
      },
      tiles() {
        return ![ 'wheels', 'tyres', 'cages' ].includes( this.step.id );
      },
      colourLabel() {
        return finishLabel( this.step.id );
      },
      referenceSide() {
        const entry = this.selection;
        if ( !entry || !entry.item ) return null;
        const weight = partWeight( entry );
        const price = entryPrice( entry );
        return weight && price > 0 ? { grams: weight.grams, price } : null;
      },
      rows() {
        const search = normalize( this.search );
        const type = this.step.title;
        return this.items
          .map(( item, index ) => ({ item, index }))
          .filter(({ item }) => {
            if ( search && !normalize( item.name || '' ).includes( search )) return false;
            if ( !this.categories.length || this.category === 'All' || item.noPedals || item.noCages ) return true;
            return ( item.category || 'Road' ).toLowerCase() === this.category.toLowerCase();
          })
          .map(({ item, index }) => {
            // Finishes in swatch order (the index selectedColor refers to).
            const swatches = item.noCages || item.noPedals ? [] : ( item.colors || [] )
              .map( a => a.color ).sort(( a, b ) => a.priority - b.priority )
              .map(( color, i ) => ({ color, index: i }));
            const shown = index === this.value ? this.colorIndex : 0;
            return {
            item,
            index,
            swatches: swatches.slice( 0, 6 ),
            finishes: swatches.length,
            colourName: this.chipName( ( swatches[ shown ] || {} ).color && swatches[ shown ].color.colorName ),
            src: item.noCages || item.noPedals ? '' : rowImage( type, item, shown, index === this.value ),
            line: rowLine( this.step, item ),
            delta: this.delta( item )
            };
          });
      }
    },
    watch: {
      'step.id'() {
        this.search = '';
        this.scrollToSelected();
      }
    },
    mounted() {
      this.scrollToSelected();
    },
    methods: {
      chipName( name ) {
        return name ? name.charAt( 0 ).toUpperCase() + name.slice( 1 ) : '';
      },
      // Weight and price against the build's part, at the row's cheapest finish.
      delta( item ) {
        const selected = this.referenceSide;
        const entry = this.selection;
        if ( !selected || item.noCages || item.noPedals || ( entry && entry.item && entry.item.id === item.id )) return null;
        const prices = ( item.colors || [] ).map( a => a.color && a.color.price || a.price || 0 ).filter( p => p > 0 );
        if ( !prices.length ) return null;
        const min = Math.min( ...prices );
        const cheapest = item.colors.findIndex( a => ( a.color && a.color.price || a.price || 0 ) === min );
        const weight = partWeight({ props: this.step, item, color: Math.max( cheapest, 0 ), tyreWidth: entry && entry.tyreWidth });
        return weight && weightDelta({ grams: weight.grams, price: min }, selected );
      },
      scrollToSelected() {
        if ( this.$refs.scroll ) this.$refs.scroll.scrollTop = 0;
        if ( this.$refs.list ) this.$refs.list.scrollTop = 0;
        // Measured once the sheet has slid in (220ms); mid-slide rows read lower.
        clearTimeout( this.scrollTimer );
        this.scrollTimer = setTimeout(() => {
          const scroll = this.docked ? this.$refs.list : this.$refs.scroll;
          const row = scroll && scroll.querySelector( '.part-row.is-selected' );
          if ( !row ) return;
          // Keep the card and chips in view unless the chosen row is out of sight.
          const top = row.getBoundingClientRect().top - scroll.getBoundingClientRect().top;
          if ( top > scroll.clientHeight - 48 ) scroll.scrollTop = top - scroll.clientHeight * .5;
        }, 260 );
      },
      // The three resting heights: full screen, the half sheet (58svh), closed.
      stops() {
        const screen = window.innerHeight;
        return { full: screen, half: Math.round( screen * .58 ), closed: 0 };
      },
      // The header follows the finger; on release the sheet glides to the
      // nearest stop, judged from where a flick would carry it.
      onTouchStart( e ) {
        if ( this.snapping ) return;
        const y = e.touches[ 0 ].clientY;
        this.touch = { startY: y, startH: this.$el.getBoundingClientRect().height, y, t: e.timeStamp, v: 0 };
      },
      onTouchMove( e ) {
        const touch = this.touch;
        if ( !touch ) return;
        e.preventDefault();
        const y = e.touches[ 0 ].clientY;
        const dt = Math.max( 1, e.timeStamp - touch.t );
        touch.v = .8 * (( y - touch.y ) / dt ) + .2 * touch.v;
        touch.y = y;
        touch.t = e.timeStamp;
        this.dragH = Math.min( this.stops().full, Math.max( 60, touch.startH - ( y - touch.startY )));
      },
      onTouchEnd() {
        const touch = this.touch;
        this.touch = null;
        if ( !touch || this.dragH == null ) return;
        const { full, half } = this.stops();
        const landing = this.dragH - touch.v * 180;
        this.snapTo( landing > ( full + half ) / 2 ? 'full' : landing > half * .55 ? 'half' : 'closed' );
      },
      snapTo( stop ) {
        if ( this.dragH == null ) this.dragH = this.$el.getBoundingClientRect().height;
        this.snapping = true;
        // Two frames: the start height paints before the transition target.
        requestAnimationFrame(() => requestAnimationFrame(() => { this.dragH = this.stops()[ stop ]; }));
        clearTimeout( this.snapTimer );
        this.snapTimer = setTimeout(() => {
          if ( stop === 'closed' ) this.$emit( 'close' );
          else this.$emit( 'expand', stop === 'full' );
          this.$nextTick(() => { this.dragH = null; this.snapping = false; });
        }, 300 );
      }
    }
  };
</script>

<style lang="scss">
  .part-drawer {
    display: flex; flex-direction: column; min-height: 0; background: #faf9f6;
    border-radius: 22px 22px 0 0; box-shadow: 0 -8px 30px rgba(37, 44, 34, .12); overflow: hidden;
    color: var(--builder-ink, #252c22);
  }
  .part-drawer--top { position: relative; display: flex; align-items: center; gap: 10px; flex: 0 0 auto; padding: 18px 16px 10px; touch-action: none; }
  .part-drawer.is-dragging { will-change: height; }
  .part-drawer--grip { position: absolute; top: 0; left: 50%; width: 80px; height: 20px; margin-left: -40px; }
  .part-drawer--grip::before { content: ''; position: absolute; top: 7px; left: 20px; width: 40px; height: 4px; border-radius: 2px; background: #cfd1c8; }
  .part-drawer--top h2 { font-size: 22px; font-weight: 500; line-height: 1.2; margin: 0; }
  .part-drawer--changing { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: #747c6a; }
  .part-drawer--changing b { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: var(--builder-accent, #344b35); color: #fff; font-size: 11px; }
  .part-drawer--close { margin-left: auto; display: inline-flex; align-items: center; justify-content: center; width: 42px; height: 42px; border: 1px solid var(--builder-line, #dedfd7); border-radius: 50%; background: #fff; }
  .part-drawer--scroll { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 0 16px calc(16px + env(safe-area-inset-bottom, 0px)); -webkit-overflow-scrolling: touch; }
  .part-row:focus-visible, .part-drawer--tabs button:focus-visible { outline: 2px solid var(--builder-accent, #344b35); outline-offset: 2px; }
  .part-drawer--list-head { position: sticky; top: 0; z-index: 1; margin: 14px -16px 0; padding: 0 16px; background: #faf9f6; }
  .part-drawer--tabs { display: flex; gap: 22px; border-bottom: 1px solid var(--builder-line, #dedfd7); overflow-x: auto; scrollbar-width: none; }
  .part-drawer--tabs button { flex: 0 0 auto; padding: 10px 2px; margin-bottom: -1px; font-size: 15px; color: #747c6a; border-bottom: 2px solid transparent; }
  .part-drawer--tabs button.is-active { color: var(--builder-ink, #252c22); border-bottom-color: var(--builder-accent, #344b35); }
  .part-drawer--search { width: 100%; margin: 8px 0 2px; padding: 8px 12px; border: 1px solid var(--builder-line, #dedfd7); border-radius: 10px; background: #fff; font-size: 16px; }
  .part-drawer--list { list-style: none; padding: 8px 0 0 !important; margin: 0; }
  .part-drawer--list > li { margin-bottom: 8px; }
  .part-row {
    display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px 12px; text-align: left;
    border: 1px solid var(--builder-line, #dedfd7); border-radius: 14px; background: #fff; color: inherit;
  }
  .part-row.is-selected { border-color: var(--builder-accent, #344b35); box-shadow: inset 0 0 0 1px var(--builder-accent, #344b35); background: #f3f5ef; }
  .part-row--image { flex: 0 0 auto; display: flex; align-items: center; justify-content: center; width: 56px; height: 56px; }
  .part-row--image img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .part-row--text { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
  .part-row--text strong { font-size: 15px; font-weight: 500; line-height: 1.3; }
  .part-row--text small { font-size: 13px; color: #747c6a; line-height: 1.35; font-variant-numeric: tabular-nums; }
  .part-row--delta { font-size: 12px !important; }
  .part-row--delta.win { color: var(--builder-accent, #344b35) !important; font-weight: 600; }
  .part-row--delta.win::before { content: '\2713\00a0'; }
  .part-row--check { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: var(--builder-accent, #344b35); }
  .part-drawer--empty { padding: 16px 4px; color: #747c6a; font-size: 14px; }
  .part-row--image { width: 72px; height: 72px; }
  .part-drawer--list.is-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .part-drawer--list.is-grid > li { margin: 0; }
  .part-drawer--list.is-grid > .part-drawer--empty { grid-column: 1 / -1; }
  .part-tile { position: relative; display: flex; flex-direction: column; border: 1px solid var(--builder-line, #dedfd7); border-radius: 14px; background: #fff; overflow: hidden; }
  .part-tile.is-selected { border-color: var(--builder-accent, #344b35); box-shadow: inset 0 0 0 1px var(--builder-accent, #344b35); }
  .part-tile--pick { display: flex; flex-direction: column; align-items: stretch; gap: 2px; width: 100%; padding: 10px 12px 8px; text-align: left; color: inherit; }
  .part-tile--image { display: flex; align-items: center; justify-content: center; height: 104px; margin-bottom: 6px; }
  .part-tile--image img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .part-tile--pick strong { font-size: 14px; font-weight: 500; line-height: 1.25; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .part-tile--pick small { font-size: 12px; color: #747c6a; line-height: 1.35; font-variant-numeric: tabular-nums; }
  .part-tile--pick small.part-tile--colour { color: var(--builder-accent, #344b35); font-weight: 600; }
  .part-tile--swatches { display: flex; flex-wrap: wrap; gap: 6px; padding: 0 12px 12px; }
  .part-tile--swatches button { display: inline-flex; padding: 2px; border-radius: 50%; border: 1.5px solid transparent; }
  .part-tile--swatches button.is-active { border-color: var(--builder-accent, #344b35); }
  .part-tile--swatches .b-color { width: 22px; height: 22px; margin: 0; border-radius: 50%; }
  .part-tile--swatches .b-color > span { border-width: 11px; }
  .part-tile--badge { position: absolute; top: 8px; right: 8px; }
  .part-drawer.is-docked { flex: 1 1 auto; border-radius: 0; box-shadow: none; background: #fff; }
  .part-drawer.is-docked .part-drawer--scroll { padding-top: 12px; }
  .part-drawer.is-docked .part-drawer--list-head { background: #fff; }
  /* Desktop: the selected card and its chips stay pinned; the list scrolls under them. */
  .part-drawer.is-docked .part-drawer--scroll { display: flex; flex-direction: column; overflow: hidden; padding-bottom: 0; }
  .part-drawer.is-docked .part-drawer--scroll > :not(.part-drawer--list-area) { flex: 0 0 auto; }
  .part-drawer.is-docked .part-drawer--list-area { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; margin: 0 -16px; padding: 0 16px 16px; }
  .part-drawer--actions { display: flex; align-items: center; gap: 6px; flex: 0 0 auto; padding: 10px 16px 0; }
  .part-drawer--actions button { height: 34px; padding: 0 14px; border: 1px solid var(--builder-line, #dedfd7); border-radius: 999px; font-size: 13px; background: #fff; }
  .part-drawer--actions button.is-go { margin-left: auto; background: var(--builder-accent, #344b35); border-color: var(--builder-accent, #344b35); color: #fff; }
  .part-drawer--actions button:disabled { opacity: .45; }
  .part-drawer--actions .part-drawer--changing { margin-right: auto; }
</style>
